import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import './DashboardBultos.css';
import DashboardMenuHeader from '../components/DashboardMenuHeader';
import {
  ChevronDown, ChevronRight, Search, RefreshCw, Filter, FilterX, X,
  TrendingDown, TrendingUp, Tag, Users, Download, Calendar,
  ShieldCheck, AlertTriangle, Zap, Archive,
} from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LabelList, Legend,
} from 'recharts';
import { useQuery } from '../QueryContext';
import { useAuth } from '../AuthContext';
import { clearCache } from '../utils/dashboardCache';
import FullScreenLoader from './FullScreenLoader';
import GlobalSearch from '../components/GlobalSearch';
import { calculateWorkingDays } from '../utils/timeUtils';
import * as XLSX from 'xlsx';

/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */

const filterLabels = {
  BI_PROVEEDOR: 'Casas',
  BI_MARCA: 'Marca',
  BI_LINEA: 'Línea',
  CLI_SEG_DES: 'Segmento',
  CLI_PRIN: 'Cliente',
  CLI_VEN_DES: 'Vendedor',
  CLI_CANAL: 'Canal',
  CLI_REG_VERD: 'Región Filtro',
  V_ANULADA: 'Factura Anulada',
  CLI_INACTIVO: 'Cliente Inactivo',
  BI_CLASIF2: 'Clasificación',
  BI_DESC1: 'Producto',
  CLI_TERRITORIO: 'Territorio',
  CLI_CANAL_REAL: 'Canal 2',
  BI_SEGMENTO: 'Portafolio',
  CLI_NOMBRE: 'Cliente Detalle',
  V_FACTURA: 'Factura',
  CLIENTE_ID: 'Cliente ID',
  ART_ID: 'SKU ID',
  BI_ASP: 'ASP',
};

const formatNumber = (num, isUSD = false) => {
  if (num == null) return isUSD ? '$0' : '0';
  const value = Number(num);
  const formatted = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
  return isUSD ? `$${formatted}` : formatted;
};
const formatDollar = (num) => formatNumber(num, true);

const formatK = (num, isUSD = false) => {
  if (!num && num !== 0) return isUSD ? '$0' : '0';
  const value = num || 0;
  if (Math.abs(value) >= 1000) {
    const v = (value / 1000).toFixed(0);
    return isUSD ? `$${v}K` : `${v}K`;
  }
  return formatNumber(value, isUSD);
};

const getSemaforoColor = (p) => {
  const val = Number(p || 0);
  if (val < 80) return '#8b0000';
  if (val < 90) return '#ff4d4d';
  if (val < 100) return '#ffcc00';
  return '#00ba52';
};

const getStockStatus = (stock, necesidad) => {
  const s = Number(stock) || 0;
  const n = Number(necesidad) || 0;
  if (n <= 0) return { label: 'Abastecido', color: '#00ba52', icon: <ShieldCheck size={14} /> };
  if (s <= 0) return { label: 'Emergencia', color: '#8b0000', icon: <Zap size={14} /> };
  if (s < n) return { label: 'Alerta', color: '#ff8c00', icon: <AlertTriangle size={14} /> };
  return { label: 'Abastecido', color: '#00ba52', icon: <ShieldCheck size={14} /> };
};

const MultiSelectFilter = ({ label, name, icon: Icon, options = [], selectedValues, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const totalOptions = options.length;
  const hasSelection = selectedValues?.length > 0;
  const allOptionsSelected = totalOptions > 0 && selectedValues.length === totalOptions;

  const displayValue = allOptionsSelected
    ? 'Todos'
    : hasSelection
      ? selectedValues.length === 1
        ? options.find((opt) => (opt.value || opt) === selectedValues[0])?.label || selectedValues[0]
        : `${selectedValues.length} Selec.`
      : 'Todos';

  const showSearch = options.length > 12;
  const filteredOptions = options
    .filter((opt) => String(opt.label || opt).toLowerCase().includes(searchTerm.toLowerCase()))
    .slice(0, 100);

  const areAllSelected = filteredOptions.every((opt) => selectedValues.includes(opt.value || opt));

  const handleSelectAll = () => {
    if (areAllSelected) {
      const newSelected = selectedValues.filter(
        (val) => !filteredOptions.some((opt) => (opt.value || opt) === val)
      );
      onChange(name, newSelected);
    } else {
      onChange(name, filteredOptions.map((opt) => opt.value || opt));
    }
  };

  const handleSingleToggle = useCallback(
    (optValue) => {
      const newVals = selectedValues.includes(optValue)
        ? selectedValues.filter((v) => v !== optValue)
        : [...selectedValues, optValue];
      onChange(name, newVals);
    },
    [selectedValues, onChange, name]
  );

  return (
    <div className="filter-pill-container" ref={dropdownRef}>
      <button
        className={`filter-pill-button ${hasSelection ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Icon size={12} className="pill-icon" />
        <span className="pill-label">{label}:</span>
        <span className="pill-value">{displayValue}</span>
        <ChevronDown size={11} className={`pill-arrow ${isOpen ? 'up' : ''}`} />
      </button>
      {isOpen && (
        <div className={`filter-dropdown-panel ${showSearch ? 'with-search' : ''}`}>
          {showSearch && (
            <div className="search-wrapper">
              <Search size={14} className="search-icon" />
              <input
                type="text"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>
          )}
          {filteredOptions.length > 0 && (
            <label className="checkbox-row select-all-row">
              <input type="checkbox" checked={areAllSelected} onChange={handleSelectAll} />
              <span>Seleccionar Todos ({filteredOptions.length})</span>
            </label>
          )}
          <div className="options-list">
            {filteredOptions.map((opt, i) => {
              const optValue = opt.value || opt;
              const optLabel = opt.label || opt;
              return (
                <label key={`${name}-${optValue}-${i}`} className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(optValue)}
                    onChange={() => handleSingleToggle(optValue)}
                  />
                  <span>{optLabel}</span>
                </label>
              );
            })}
          </div>
          {hasSelection && (
            <button className="reset-pill-link" onClick={() => onChange(name, [])}>
              Limpiar ({selectedValues.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const nombresMeses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
                      'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const INITIAL_FILTERS = {
  BI_PROVEEDOR: [], BI_MARCA: [], BI_LINEA: [], CLI_SEG_DES: [],
  CLI_PRIN: [], CLI_VEN_DES: [], CLI_CANAL: [], CLI_REG_VERD: [],
  V_ANULADA: [], CLI_INACTIVO: [], BI_CLASIF2: [], BI_DESC1: [],
  CLI_TERRITORIO: [], CLI_CANAL_REAL: [], BI_SEGMENTO: [], CLI_NOMBRE: [],
  V_FACTURA: [], CLIENTE_ID: [], ART_ID: [], BI_ASP: [],
};

// ═══════════════════════════════════════════════════════════════
// 🎯 Tabla de Concentración (sin cambios)
// ═══════════════════════════════════════════════════════════════
const ConcentracionTable = ({ title, subtitle, rows, showSkus = false }) => {
  const totals = useMemo(
    () =>
      (rows || []).reduce(
        (acc, r) => {
          acc.bultos += Number(r.bultos || 0);
          acc.usd += Number(r.usd || 0);
          acc.skus += Number(r.skus_distintos || 0);
          return acc;
        },
        { bultos: 0, usd: 0, skus: 0 }
      ),
    [rows]
  );

  return (
    <div className="chart-container mini table-fixed-height concentracion-chart" style={{ flex: 1 }}>
      <div className="table-header-container">
        <div>
          <h3 className="chart-title">{title}</h3>
          {subtitle && <span className="chart-subtitle">{subtitle}</span>}
        </div>
      </div>
      <div className="minimal-table-wrapper">
        <div className={`table-header5 concentracion-cols ${showSkus ? 'with-skus' : ''}`}>
          <div className="col-name">Canal</div>
          <div className="col-data">Bultos</div>
          <div className="col-data">% Part. Bultos</div>
          <div className="col-data">USD</div>
          <div className="col-data">% Part. USD</div>
          {showSkus && <div className="col-data">SKUs</div>}
        </div>
        <div className="table-body5">
          {rows && rows.length > 0 ? (
            rows.map((row, i) => {
              const pb = Number(row.pct_bultos || 0);
              const pu = Number(row.pct_usd || 0);
              return (
                <div className={`table-row5 concentracion-cols ${showSkus ? 'with-skus' : ''}`} key={`cc-${i}`}>
                  <div className="col-name truncate" title={row.canal}>{row.canal || '---'}</div>
                  <div className="col-data">{formatNumber(row.bultos)}</div>
                  <div className="col-data bold">
                    <div className="pct-bar-wrapper">
                      <div className="pct-bar-bg">
                        <div className="pct-bar pct-bar-bultos" style={{ width: `${Math.min(pb, 100)}%` }} />
                      </div>
                      <span className="pct-text">{pb.toFixed(2)}%</span>
                    </div>
                  </div>
                  <div className="col-data">{formatDollar(row.usd)}</div>
                  <div className="col-data bold">
                    <div className="pct-bar-wrapper">
                      <div className="pct-bar-bg">
                        <div className="pct-bar pct-bar-usd" style={{ width: `${Math.min(pu, 100)}%` }} />
                      </div>
                      <span className="pct-text">{pu.toFixed(2)}%</span>
                    </div>
                  </div>
                  {showSkus && <div className="col-data">{formatNumber(row.skus_distintos || 0)}</div>}
                </div>
              );
            })
          ) : (
            <div className="concentracion-empty">Sin datos para el período seleccionado</div>
          )}
        </div>
        <div className={`table-footer5 concentracion-cols ${showSkus ? 'with-skus' : ''}`}>
          <div className="col-name">TOTALES</div>
          <div className="col-data">{formatNumber(totals.bultos)}</div>
          <div className="col-data bold">100.00%</div>
          <div className="col-data">{formatDollar(totals.usd)}</div>
          <div className="col-data bold">100.00%</div>
          {showSkus && <div className="col-data">{formatNumber(totals.skus)}</div>}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// 🎯 CustomTooltip para el gráfico Real vs Stock (estilo Asesores)
// ═══════════════════════════════════════════════════════════════
const FarmatodoInvTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const realItem = payload.find((p) => p.name === 'Real Venta');
  const stockItem = payload.find((p) => p.name === 'Stock 002');
  return (
    <div
      className="custom-tooltip"
      style={{
        backgroundColor: 'white',
        padding: '10px',
        borderRadius: '5px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
        border: '1px solid #ccc',
      }}
    >
      <p style={{ color: '#333', fontWeight: 'bold', marginBottom: '5px' }}>Día {label}</p>
      {realItem && (
        <p style={{ color: '#0068b3', fontSize: '0.8rem', margin: 0 }}>
          Real Venta: {formatNumber(realItem.value)}
        </p>
      )}
      {stockItem && (
        <p style={{ color: '#f59e0b', fontSize: '0.8rem', margin: '3px 0 0' }}>
          Stock 002: {formatNumber(stockItem.value)}
        </p>
      )}
    </div>
  );
};

const Farmatodo = () => {
  const {
    salesQueryPML,
    salesQueryFarmatodo,
    inventarioFarmatodoTablaQuery,
    inventarioFarmatodoGraficoQuery,
    getSalesFilters,
    loading: apiLoading,
  } = useQuery();
  const { profile } = useAuth();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [localLoading, setLocalLoading] = useState(true);
  const [querying, setQuerying] = useState(false);
  const [proveedorMarcaLineaTotals, setProveedorMarcaLineaTotals] = useState({
    cuota_bultos: 0, real_bultos: 0, cuota_usd: 0, real_usd: 0,
  });
  const [metricType, setMetricType] = useState('BULTOS');
  const [breakdownType, setBreakdownType] = useState('REG_ASP');
  const [selectedMeses, setSelectedMeses] = useState([nombresMeses[new Date().getMonth()]]);
  const [selectedDay, setSelectedDay] = useState([]);
  const currentYear = new Date().getFullYear();
  const [selectedAnios, setSelectedAnios] = useState([currentYear]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [availableOptions, setAvailableOptions] = useState({});
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [salesConfig, setSalesConfig] = useState(null);
  const [comparisonMode, setComparisonMode] = useState('CUOTA');
  const [devMode, setDevMode] = useState('NONE');
  const [expandedMarcas, setExpandedMarcas] = useState({});
  const [proveedorMarcaLineaTableData, setProveedorMarcaLineaTableData] = useState([]);
  const requestRef = useRef(0);
  const filtersInitialized = useRef(false);
  const [workDayMetrics, setWorkDayMetrics] = useState({ total: 0, elapsed: 0, percentage: 0 });
  const [lowCompliance, setLowCompliance] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);

  // Tablas de concentración
  const [concentracionCanal, setConcentracionCanal] = useState([]);
  const [concentracionCanalFarmatodo, setConcentracionCanalFarmatodo] = useState([]);
  const [metaFarmatodo, setMetaFarmatodo] = useState(null);

  // 🆕 Inventario Farmatodo
  const [farmatodoInvChart, setFarmatodoInvChart] = useState([]);
  const [farmatodoInvTable, setFarmatodoInvTable] = useState([]);
  const [farmatodoInvMeta, setFarmatodoInvMeta] = useState(null);
  const [farmatodoInvLoading, setFarmatodoInvLoading] = useState(false);
  const [expandedInvBrands, setExpandedInvBrands] = useState({});
  const [invUnitMode, setInvUnitMode] = useState('BULTOS');
  const [showOnlyAlertEmergency, setShowOnlyAlertEmergency] = useState(false);

  const toggleMarca = (marca) => setExpandedMarcas((prev) => ({ ...prev, [marca]: !prev[marca] }));
  const toggleInvBrand = (marca) => setExpandedInvBrands((prev) => ({ ...prev, [marca]: !prev[marca] }));

  useEffect(() => {
    const metrics = calculateWorkingDays(selectedMeses, selectedAnios, nombresMeses);
    setWorkDayMetrics(metrics);
  }, [selectedMeses, selectedAnios]);

  useEffect(() => {
    const loadDashboardData = async () => {
      setLocalLoading(true);
      try {
        const config = await getSalesFilters();
        if (!config) throw new Error('Configuración de ventas no disponible.');
        setSalesConfig(config);
        setAvailableOptions(config.availableOptions || {});
        setAvailableYears(config.availableYears || [currentYear]);

        const allSegments = config.availableOptions?.CLI_SEG_DES || [];
        const excluded = ['DONACIONES', 'EMPLEADOS', 'NO APLICA'];
        const prefiltered = allSegments.filter(
          (s) => !excluded.some((ex) => s.toUpperCase().includes(ex.toUpperCase()))
        );

        setFilters({
          ...INITIAL_FILTERS,
          V_ANULADA: ['0'],
          CLI_INACTIVO: ['0'],
          CLI_SEG_DES: prefiltered,
        });

        const defaultYear = config.currentYear || currentYear;
        setSelectedAnios([defaultYear]);
        setSelectedMeses([nombresMeses[new Date().getMonth()]]);
        filtersInitialized.current = true;
      } catch (err) {
        console.error('Error al cargar la configuración:', err);
      } finally {
        setLocalLoading(false);
      }
    };
    loadDashboardData();
  }, []);

  const handleRefresh = async () => {
    clearCache();
    await fetchData();
  };

  const handleResetFilters = useCallback(() => {
    if (!salesConfig?.availableOptions?.CLI_SEG_DES) return;
    const allSegments = salesConfig.availableOptions.CLI_SEG_DES;
    const excluded = ['DONACIONES', 'EMPLEADOS', 'NO APLICA'];
    const prefiltered = allSegments.filter(
      (s) => !excluded.some((ex) => s.toUpperCase().includes(ex.toUpperCase()))
    );
    setFilters({
      ...INITIAL_FILTERS,
      V_ANULADA: ['0'],
      CLI_INACTIVO: ['0'],
      CLI_SEG_DES: prefiltered,
    });
  }, [salesConfig]);

  // ── Procesar respuesta PML (sin cambios) ─────────────────────
  const processResponse = useCallback((response) => {
    const rows = response.proveedor_marca_linea_table || [];
    const hasThirdLevel = rows.some((row) => row.col3_name && row.col3_name.trim() !== '');

    const calcVariacion = (actual, prev) => {
      if (prev == null || isNaN(prev)) return null;
      if (prev === 0) return actual > 0 ? 100 : 0;
      return ((actual - prev) / prev) * 100;
    };

    const tree = {};
    for (let i = 0, len = rows.length; i < len; i++) {
      const row = rows[i];
      const l1 = row.col1_name || 'SIN_ENTIDAD';
      const l2 = row.col2_name || 'SIN_ENTIDAD2';
      const l3 = hasThirdLevel ? row.col3_name || 'SIN_CLIENTE' : null;
      const linea = row.BI_LINEA || 'SIN LÍNEA';
      const prevB = Number(row.real_bultos_prev ?? 0);
      const prevU = Number(row.real_usd_prev ?? 0);

      if (!tree[l1])
        tree[l1] = {
          name: l1, cuota_bultos: 0, real_bultos: 0, cuota_usd: 0, real_usd: 0,
          real_bultos_prev: 0, real_usd_prev: 0, marcas: {},
        };
      const n1 = tree[l1];
      n1.cuota_bultos += Number(row.cuota_bultos || 0);
      n1.real_bultos += Number(row.real_bultos || 0);
      n1.cuota_usd += Number(row.cuota_usd || 0);
      n1.real_usd += Number(row.real_usd || 0);
      n1.real_bultos_prev += prevB;
      n1.real_usd_prev += prevU;

      if (!n1.marcas[l2])
        n1.marcas[l2] = {
          name: l2, cuota_bultos: 0, real_bultos: 0, cuota_usd: 0, real_usd: 0,
          real_bultos_prev: 0, real_usd_prev: 0,
          ...(hasThirdLevel ? { clientes: {} } : { lineas: [] }),
        };
      const n2 = n1.marcas[l2];
      n2.cuota_bultos += Number(row.cuota_bultos || 0);
      n2.real_bultos += Number(row.real_bultos || 0);
      n2.cuota_usd += Number(row.cuota_usd || 0);
      n2.real_usd += Number(row.real_usd || 0);
      n2.real_bultos_prev += prevB;
      n2.real_usd_prev += prevU;

      if (hasThirdLevel) {
        if (!n2.clientes[l3])
          n2.clientes[l3] = {
            name: l3, cuota_bultos: 0, real_bultos: 0, cuota_usd: 0, real_usd: 0,
            real_bultos_prev: 0, real_usd_prev: 0, lineas: [],
          };
        const n3 = n2.clientes[l3];
        n3.cuota_bultos += Number(row.cuota_bultos || 0);
        n3.real_bultos += Number(row.real_bultos || 0);
        n3.cuota_usd += Number(row.cuota_usd || 0);
        n3.real_usd += Number(row.real_usd || 0);
        n3.real_bultos_prev += prevB;
        n3.real_usd_prev += prevU;
        n3.lineas.push({ name: linea, ...row });
      } else {
        n2.lineas.push({ name: linea, ...row });
      }
    }

    const processLevel = (arr) => {
      const sorted = arr.sort((a, b) => b.real_bultos - a.real_bultos);
      for (const item of sorted) {
        item.var_bultos = calcVariacion(item.real_bultos, item.real_bultos_prev);
        item.var_usd = calcVariacion(item.real_usd, item.real_usd_prev);
        if (item.marcas) {
          item.marcas = Object.values(item.marcas)
            .map((m) => {
              m.var_bultos = calcVariacion(m.real_bultos, m.real_bultos_prev);
              m.var_usd = calcVariacion(m.real_usd, m.real_usd_prev);
              if (m.clientes) {
                m.clientes = Object.values(m.clientes)
                  .map((c) => {
                    c.var_bultos = calcVariacion(c.real_bultos, c.real_bultos_prev);
                    c.var_usd = calcVariacion(c.real_usd, c.real_usd_prev);
                    c.lineas = c.lineas
                      .sort((a, b) => b.real_bultos - a.real_bultos)
                      .map((l) => ({
                        ...l,
                        var_bultos: calcVariacion(l.real_bultos || 0, Number(l.real_bultos_prev ?? 0)),
                        var_usd: calcVariacion(l.real_usd || 0, Number(l.real_usd_prev ?? 0)),
                      }));
                    return c;
                  })
                  .sort((a, b) => b.real_bultos - a.real_bultos);
              } else if (m.lineas) {
                m.lineas = m.lineas
                  .sort((a, b) => b.real_bultos - a.real_bultos)
                  .map((l) => ({
                    ...l,
                    var_bultos: calcVariacion(l.real_bultos || 0, Number(l.real_bultos_prev ?? 0)),
                    var_usd: calcVariacion(l.real_usd || 0, Number(l.real_usd_prev ?? 0)),
                  }));
              }
              return m;
            })
            .sort((a, b) => b.real_bultos - a.real_bultos);
        }
      }
      return sorted;
    };

    const processedData = processLevel(Object.values(tree));
    const pmlTotalsRaw = response.proveedor_marca_linea_total_table?.[0] || {};

    return {
      proveedorMarcaLineaTableData: processedData,
      proveedorMarcaLineaTotals: {
        cuota_bultos: Number(pmlTotalsRaw.total_cuota_bultos || 0),
        real_bultos: Number(pmlTotalsRaw.total_real_bultos || 0),
        cuota_usd: Number(pmlTotalsRaw.total_cuota_usd || 0),
        real_usd: Number(pmlTotalsRaw.total_real_usd || 0),
        real_bultos_prev: Number(pmlTotalsRaw.total_real_bultos_prev || 0),
        real_usd_prev: Number(pmlTotalsRaw.total_real_usd_prev || 0),
      },
    };
  }, []);

  // ── Agrupación jerárquica de la tabla inventario (por marca) ──
  const farmatodoInvGrouped = useMemo(() => {
    const grouped = farmatodoInvTable.reduce((acc, row) => {
      const brand = row.marca || row.producto || 'SIN MARCA';
      if (!acc[brand]) {
        acc[brand] = {
          marca: brand,
          cuota_forecast: 0,
          real_venta: 0,
          inventario: 0,
          necesidad: 0,
          venta_diaria_3m: 0,
          productos: [],
        };
      }
      const cuota = Number(row.cuota_forecast || 0);
      const real = Number(row.real_venta || 0);
      const inv = Number(row.inventario || 0);
      const nec = Number(row.necesidad || 0);
      const vd3m = Number(row.venta_diaria_3m || 0);

      acc[brand].cuota_forecast += cuota;
      acc[brand].real_venta += real;
      acc[brand].inventario += inv;
      acc[brand].necesidad += nec;
      acc[brand].venta_diaria_3m += vd3m;

      acc[brand].productos.push({
        ...row,
        cuota_forecast: cuota,
        real_venta: real,
        inventario: inv,
        necesidad: nec,
        dias_inventario: Number(row.dias_inventario || 0),
        cumplimiento_pct: cuota > 0 ? (real / cuota) * 100 : 0,
      });
      return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => b.real_venta - a.real_venta);
  }, [farmatodoInvTable]);

  // ── Export concentración (sin cambios) ───────────────────────
  const exportConcentracion = useCallback((rows, filename, showSkus) => {
    const data = rows.map((r) => ({
      Canal: r.canal,
      Bultos: Math.round(Number(r.bultos || 0)),
      'Pct Bultos': Number(r.pct_bultos || 0).toFixed(2) + '%',
      USD: Math.round(Number(r.usd || 0)),
      'Pct USD': Number(r.pct_usd || 0).toFixed(2) + '%',
      ...(showSkus ? { SKUs: Number(r.skus_distintos || 0) } : {}),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Concentracion');
    XLSX.writeFile(wb, `${filename}_${new Date().getTime()}.xlsx`);
  }, []);

  // ── Export tabla inventario ─────────────────────────────────
  const exportInvTable = useCallback(() => {
    const rows = [];
    farmatodoInvGrouped.forEach((brand) => {
      rows.push({
        Nivel: 'MARCA',
        Identificador: brand.marca,
        Cuota: Math.round(brand.cuota_forecast),
        Real: Math.round(brand.real_venta),
        Cumplimiento: (brand.cuota_forecast > 0 ? ((brand.real_venta / brand.cuota_forecast) * 100).toFixed(1) : 0) + '%',
        Stock: Math.round(brand.inventario),
        Necesidad: Math.round(brand.necesidad),
      });
      brand.productos.forEach((p) => {
        rows.push({
          Nivel: 'PRODUCTO',
          Identificador: p.producto,
          Cuota: Math.round(p.cuota_forecast),
          Real: Math.round(p.real_venta),
          Cumplimiento: p.cumplimiento_pct.toFixed(1) + '%',
          Stock: Math.round(p.inventario),
          Necesidad: Math.round(p.necesidad),
        });
      });
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventario FARMATODO');
    XLSX.writeFile(wb, `Inventario_FARMATODO_${new Date().toISOString().split('T')[0]}.xlsx`);
  }, [farmatodoInvGrouped]);

  // ── FETCH principal (paralelo: PML + Farmatodo + Inv tabla + Inv gráfico) ──
  const fetchData = useCallback(async () => {
    if (!filtersInitialized.current) return;
    const requestId = ++requestRef.current;
    setQuerying(true);
    setFarmatodoInvLoading(true);

    try {
      const payload = {
        metric_type: metricType,
        comparison_mode: comparisonMode,
        dev_mode: devMode,
        page_anios: selectedAnios.filter((a) => a),
        page_meses: selectedMeses.filter((m) => m),
        page_dias: selectedDay.filter((d) => d),
        filters: filters,
        breakdown_type: breakdownType,
        chart_year: selectedAnios?.[0] || null,
        low_compliance: lowCompliance,
        user_info: profile ? { username: profile.username, cargo: profile.cargo } : null,
      };

      // Payload específico para inventario Farmatodo (limpiamos filtros redundantes)
      const cleanInvFilters = { ...filters };
      delete cleanInvFilters.CLI_PRIN;   // ya se fuerza a FARMATODO en backend
      delete cleanInvFilters.CLI_SEG_DES; // ya se excluyen segmentos especiales en backend

      const invPayload = {
        metric_display_type: invUnitMode,
        comparison_mode: comparisonMode,
        page_anios: selectedAnios.filter((a) => a),
        page_meses: selectedMeses.filter((m) => m).map((m) => m.toUpperCase()),
        filters: cleanInvFilters,
        user_info: profile ? { username: profile.username, cargo: profile.cargo } : null,
      };

      const [rawResponse, farmatodoResponse, invChartRes, invTableRes] = await Promise.all([
        salesQueryPML(payload),
        salesQueryFarmatodo(payload),
        inventarioFarmatodoGraficoQuery(invPayload),
        inventarioFarmatodoTablaQuery(invPayload),
      ]);

      if (requestId !== requestRef.current) return;

      // PML
      const processed = processResponse(rawResponse);
      setProveedorMarcaLineaTableData(processed.proveedorMarcaLineaTableData);
      setProveedorMarcaLineaTotals(processed.proveedorMarcaLineaTotals);

      // Concentración Farmatodo
      if (farmatodoResponse && farmatodoResponse.status !== 'cancelled') {
        setConcentracionCanal(farmatodoResponse.concentracion_canal || []);
        setConcentracionCanalFarmatodo(farmatodoResponse.concentracion_canal_farmatodo || []);
        setMetaFarmatodo(farmatodoResponse.meta || null);
      }

      // Inventario Farmatodo
      if (invChartRes?.data) setFarmatodoInvChart(invChartRes.data);
      if (invTableRes?.data) {
        setFarmatodoInvTable(invTableRes.data);
        if (invTableRes.meta) setFarmatodoInvMeta(invTableRes.meta);
      }
    } catch (err) {
      if (requestId === requestRef.current) console.error('❌ Error fetchData:', err);
    } finally {
      if (requestId === requestRef.current) {
        setQuerying(false);
        setFarmatodoInvLoading(false);
      }
    }
  }, [
    comparisonMode, devMode, selectedMeses, selectedAnios, selectedDay, filters,
    lowCompliance, metricType, breakdownType, invUnitMode,
    salesQueryPML, salesQueryFarmatodo,
    inventarioFarmatodoTablaQuery, inventarioFarmatodoGraficoQuery,
    profile, processResponse,
  ]);

  const debouncedFetch = useRef();
  useEffect(() => {
    if (!filtersInitialized.current) return;
    debouncedFetch.current = setTimeout(() => fetchData(), 300);
    return () => clearTimeout(debouncedFetch.current);
  }, [comparisonMode, devMode, selectedMeses, selectedAnios, selectedDay, filters, lowCompliance, metricType, breakdownType, invUnitMode]);

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };

  const farmatodoInvTotals = useMemo(() => {
    return farmatodoInvGrouped.reduce(
      (acc, b) => {
        acc.cuota += b.cuota_forecast;
        acc.real += b.real_venta;
        acc.inv += b.inventario;
        acc.nec += b.necesidad;
        acc.vd3m += b.venta_diaria_3m;
        return acc;
      },
      { cuota: 0, real: 0, inv: 0, nec: 0, vd3m: 0 }
    );
  }, [farmatodoInvGrouped]);

  // ================== RENDER PRINCIPAL ==================
  return (
    <motion.div
      className="dashboard-container"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
      transition={isMobile ? { duration: 0.3 } : { duration: 0.6, ease: 'easeInOut' }}
    >
      <DashboardMenuHeader />

      {(apiLoading || localLoading) && !filtersInitialized.current ? (
        <FullScreenLoader message="Cargando datos del servidor..." />
      ) : null}
      {querying && <div className="querying-overlay active" />}

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={isMobile ? { duration: 0.2 } : { duration: 0.5, ease: 'easeOut' }}
      >
        <header className="header-flex">
          <div className="title-section">
            <h1 className="dashboard-title">Analisis Farmatodo</h1>
            <div className="title-underline"></div>
          </div>
          <div className="date-selectors">
            <button className="refresh-button" onClick={handleRefresh} title="Actualizar Datos" disabled={querying}>
              <RefreshCw size={16} className={querying ? 'spin-anim' : ''} />
            </button>
            <button className="mobile-filter-btn" onClick={() => setShowMobileFilters(true)}>
              <Filter size={16} />
              <span>Filtros</span>
            </button>
            <div className="switch-toggle-group">
              <button
                className={`switch-toggle-btn ${comparisonMode === 'CUOTA' ? 'active' : ''}`}
                onClick={() => setComparisonMode('CUOTA')}
              >
                Cuota
              </button>
              <button
                className={`switch-toggle-btn ${comparisonMode === 'FORECAST' ? 'active' : ''}`}
                onClick={() => setComparisonMode('FORECAST')}
              >
                Forecast
              </button>
            </div>

            <div className="date-filters-group">
              <MultiSelectFilter
                label="Día"
                name="dias"
                icon={Calendar}
                options={Array.from({ length: 31 }, (_, i) => (i + 1).toString())}
                selectedValues={selectedDay}
                onChange={(name, vals) => setSelectedDay(vals)}
              />
              <MultiSelectFilter
                label="Mes"
                name="meses"
                icon={Calendar}
                options={nombresMeses}
                selectedValues={selectedMeses}
                onChange={(name, vals) => setSelectedMeses(vals)}
              />
              <MultiSelectFilter
                label="Año"
                name="anios"
                icon={Calendar}
                options={availableYears.map((year) => ({ value: year, label: year.toString() }))}
                selectedValues={selectedAnios}
                onChange={(name, vals) => setSelectedAnios(vals)}
              />
            </div>
            <div style={isMobile ? { width: '100%', marginTop: '8px' } : {}}>
              <GlobalSearch
                availableOptions={availableOptions}
                filterLabels={filterLabels}
                filters={filters}
                setFilters={setFilters}
              />
            </div>
          </div>
        </header>

        {(() => {
          const bultosCumplimiento =
            Number(proveedorMarcaLineaTotals.cuota_bultos) > 0
              ? (Number(proveedorMarcaLineaTotals.real_bultos) / Number(proveedorMarcaLineaTotals.cuota_bultos)) * 100
              : 0;
          const timeVsSalesDiff = bultosCumplimiento - Number(workDayMetrics.percentage);
          return (
            <div className="working-days-bar">
              <div className="wd-item"><span>Días Hábiles:</span> <strong>{workDayMetrics.total}</strong></div>
              <div className="wd-item"><span>Transcurridos:</span> <strong>{workDayMetrics.elapsed}</strong></div>
              <div className="wd-item"><span>Avance:</span> <strong>{workDayMetrics.percentage}%</strong></div>
              <div className="wd-separator"></div>
              <div className={`wd-item variation-item ${timeVsSalesDiff >= 0 ? 'positive' : 'negative'}`}>
                {timeVsSalesDiff >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                <strong>{timeVsSalesDiff.toFixed(1)}%</strong>
                <span className="wd-label">(vs Avance)</span>
              </div>
            </div>
          );
        })()}

        {/* Sidebar filtros (sin cambios) */}
        <div className={`filters-sidebar-container ${showMobileFilters ? 'open' : ''}`}>
          <div className="filters-sidebar-header">
            <h3>Filtros Disponibles</h3>
            <div className="filters-header-actions">
              <button className="reset-filters-btn" onClick={handleResetFilters} title="Restablecer filtros">
                <FilterX size={16} />
              </button>
              <button className="close-filters-btn" onClick={() => setShowMobileFilters(false)}>
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="filters-grid" style={{ gap: '8px' }}>
            {Object.keys(filters).map((key) => (
              <MultiSelectFilter
                key={key}
                label={filterLabels[key] || key}
                name={key}
                icon={key.includes('CLI') ? Users : Tag}
                options={availableOptions[key] || []}
                selectedValues={filters[key] || []}
                onChange={(name, vals) => setFilters((prev) => ({ ...prev, [name]: vals }))}
              />
            ))}
            <div className="filter-pill-container">
              <button
                className={`filter-pill-button ${lowCompliance ? 'active low-compliance' : ''}`}
                onClick={() => setLowCompliance(!lowCompliance)}
                title="Filtrar por cumplimiento menor al 80%"
              >
                <span className="pill-label">Cumplimiento &lt; 80%</span>
              </button>
            </div>
            <button className="filter-pill-button reset-filters" onClick={handleResetFilters}>
              <FilterX size={12} className="pill-icon" />
              <span className="pill-label">Limpiar</span>
            </button>
          </div>
        </div>
        {showMobileFilters && <div className="filters-overlay" onClick={() => setShowMobileFilters(false)} />}

        {/* ═══════════════════════════════════════════════════════
            🎯 TABLAS DE CONCENTRACIÓN FARMATODO (existentes)
           ═══════════════════════════════════════════════════════ */}
        <div
          className="detail-table-container3"
          style={{
            opacity: querying ? 0.8 : 1,
            transition: 'opacity 0.3s',
            marginTop: '20px',
            height: isMobile ? 'auto' : undefined,
            overflow: isMobile ? 'visible' : undefined,
          }}
        >
          <div className="concentracion-grid-2col">
            <ConcentracionTable
              title="Concentración por Canal"
              subtitle="Participación sobre el total del universo filtrado"
              rows={concentracionCanal}
              showSkus={false}
            />
            <ConcentracionTable
              title="Cartera FARMATODO — Concentración por Canal"
              subtitle={
                metaFarmatodo
                  ? `Solo SKUs con venta en FARMATODO · ${formatNumber(metaFarmatodo.skus_farmatodo)} SKUs`
                  : 'Solo SKUs con venta en FARMATODO'
              }
              rows={concentracionCanalFarmatodo}
              showSkus={true}
            />
          </div>

          <div className="concentracion-actions">
            <button
              className="export-table-btn"
              onClick={() => exportConcentracion(concentracionCanal, 'Concentracion_Canales', false)}
              disabled={!concentracionCanal.length}
            >
              <Download size={12} />
              <span style={{ marginLeft: '6px' }}>Tabla 1</span>
            </button>
            <button
              className="export-table-btn"
              onClick={() => exportConcentracion(concentracionCanalFarmatodo, 'Concentracion_Cartera_FARMATODO', true)}
              disabled={!concentracionCanalFarmatodo.length}
            >
              <Download size={12} />
              <span style={{ marginLeft: '6px' }}>Tabla 2</span>
            </button>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════
            🎯 SECCIÓN INVENTARIO FARMATODO · ALMACÉN 002
           ═══════════════════════════════════════════════════════ */}
        <div
          style={{
            marginTop: '32px',
            paddingTop: '16px',
            borderTop: '2px solid rgba(255,255,255,0.08)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '8px',
              marginBottom: '12px',
            }}
          >
            <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#fff', fontWeight: 500 }}>
              Inventario FARMATODO · Almacén 002
            </h2>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <div className="switch-toggle-group">
                <button
                  className={`switch-toggle-btn ${invUnitMode === 'BULTOS' ? 'active' : ''}`}
                  onClick={() => setInvUnitMode('BULTOS')}
                >
                  Bultos
                </button>
                <button
                  className={`switch-toggle-btn ${invUnitMode === 'UNIDADES' ? 'active' : ''}`}
                  onClick={() => setInvUnitMode('UNIDADES')}
                >
                  Unidades
                </button>
              </div>
              <button
                className="export-table-btn"
                onClick={exportInvTable}
                disabled={farmatodoInvGrouped.length === 0}
                title="Exportar tabla análisis inventario"
              >
                <Download size={12} />
                <span style={{ marginLeft: '6px' }}>Exportar</span>
              </button>
            </div>
          </div>

          {/* ── GRÁFICO (Real diaria + Stock 002) ────────────── */}
          <div
            className="full-width-chart-container"
            style={{ opacity: farmatodoInvLoading ? 0.7 : 1, transition: 'opacity 0.3s' }}
          >
            <div className="asesores-chart-header">
              <div className="chart-header-title">
                <h3 className="chart-title">
                  Real Venta Diaria vs Stock 002 ({invUnitMode === 'BULTOS' ? 'Bultos' : 'Unidades'})
                </h3>
              </div>
            </div>
            {farmatodoInvLoading ? (
            <div className="loading-table">
              <RefreshCw className="spin-anim" size={32} />
              <span>Cargando gráfico...</span>
            </div>
          ) : farmatodoInvChart.length === 0 ? (
            <div className="empty-state">
              <Archive size={48} />
              <h4>Sin datos para el período</h4>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart
                data={farmatodoInvChart}
                margin={{ top: 20, right: 10, left: -10, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ffffff20" />
                <XAxis
                  dataKey="dia"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: '#fff' }}
                />
                {/* Eje izquierdo: Venta diaria (barras) */}
                <YAxis
                  yAxisId="left"
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => formatK(v)}
                  tick={{ fontSize: 10, fill: '#fff' }}
                />
                {/* Eje derecho: Stock (línea) — escala independiente */}
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => formatK(v)}
                  tick={{ fontSize: 10, fill: '#fbbf24' }}
                  width={50}
                />
                <Tooltip content={<FarmatodoInvTooltip />} />
                <Legend
                  verticalAlign="top"
                  align="right"
                  iconType="circle"
                  wrapperStyle={{ paddingBottom: '15px', fontSize: '11px', fill: '#fff' }}
                />
                {/* 🎯 Barra: SOLO venta diaria */}
                <Bar
                  yAxisId="left"
                  name="Real Venta"
                  dataKey="real_venta"
                  fill="#0068b3"
                  radius={[4, 4, 0, 0]}
                  barSize={22}
                >
                  <LabelList
                    dataKey="real_venta"
                    position="top"
                    formatter={(v) => (v > 0 ? formatK(v) : '')}
                    style={{ fontSize: '10px', fontWeight: 'bold', fill: '#fff' }}
                  />
                </Bar>
                {/* 🎯 Línea: SOLO stock 002 */}
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="stock_disponible"
                  name="Stock 002"
                  stroke="#f59e0b"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: '#f59e0b' }}
                  activeDot={{ r: 5 }}
                  >
                <LabelList
                    dataKey="stock_disponible"
                    position="top"
                    formatter={(v) => (v > 0 ? formatK(v) : '')}
                    style={{ fontSize: '10px', fontWeight: 'bold', fill: '#f59e0b' }}
                  />

                </Line>
              </ComposedChart>
            </ResponsiveContainer>
          )}
                    </div>

          {/* ── TABLA (Análisis por marca → producto) ────────── */}
          <div className="detail-table-container" style={{ marginTop: '24px' }}>
            <div className="table-header">
              <h3>Análisis Producto vs Cuota vs Stock 002</h3>
              <div className="table-controls">
                <button
                  className={`switch-toggle-btn ${showOnlyAlertEmergency ? 'active' : ''}`}
                  style={{ padding: '6px 10px' }}
                  onClick={() => setShowOnlyAlertEmergency((v) => !v)}
                >
                  Solo Alerta/Emergencia
                </button>
                <span>Marcas: {farmatodoInvGrouped.length}</span>
              </div>
            </div>

            {farmatodoInvLoading ? (
              <div className="loading-table">
                <RefreshCw className="spin-anim" size={32} />
                <span>Cargando análisis...</span>
              </div>
            ) : farmatodoInvGrouped.length === 0 ? (
              <div className="empty-state">
                <Archive size={48} />
                <h4>No hay datos de análisis</h4>
                <p>Ajusta los filtros para visualizar el inventario</p>
              </div>
            ) : (
              <div className="responsive-table-wrapper">
                <table className="detail-table analysis-table analysis-mobile-fix">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}></th>
                      <th>Marca / Producto</th>
                      <th className="number">{comparisonMode}</th>
                      <th className="number">Real</th>
                      <th className="number">% Cumpl.</th>
                      <th className="number">Stock 002</th>
                      <th className="number">Días Inv.</th>
                      <th className="number">Necesidad</th>
                      <th className="number">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {farmatodoInvGrouped.map((brand, bIdx) => {
                      const isExpanded = expandedInvBrands[brand.marca];
                      const compl =
                        brand.cuota_forecast > 0
                          ? (brand.real_venta / brand.cuota_forecast) * 100
                          : 0;
                      const diasInvBrand =
                        brand.venta_diaria_3m > 0
                          ? brand.inventario / brand.venta_diaria_3m
                          : 999;
                      const brandStatus = getStockStatus(brand.inventario, brand.necesidad);

                      const showBrand = !showOnlyAlertEmergency
                        ? true
                        : ['Alerta', 'Emergencia'].includes(brandStatus.label) ||
                          brand.productos.some((p) =>
                            ['Alerta', 'Emergencia'].includes(
                              getStockStatus(p.inventario, p.necesidad).label
                            )
                          );

                      if (!showBrand) return null;

                      return (
                        <React.Fragment key={`inv-brand-${bIdx}`}>
                          <tr className="parent-row pointer" onClick={() => toggleInvBrand(brand.marca)}>
                            <td>{isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</td>
                            <td className="bold">{brand.marca}</td>
                            <td className="number">{formatNumber(brand.cuota_forecast)}</td>
                            <td className="number">{formatNumber(brand.real_venta)}</td>
                            <td className="number bold">
                              <div className="status-with-dot">
                                <span>{compl.toFixed(0)}%</span>
                                <div
                                  className="status-dot"
                                  style={{ backgroundColor: getSemaforoColor(compl), margin: 0 }}
                                ></div>
                              </div>
                            </td>
                            <td className="number">{formatNumber(brand.inventario)}</td>
                            <td
                              className="number bold"
                              style={{ color: diasInvBrand < 15 ? '#990000' : '#187900' }}
                            >
                              {diasInvBrand >= 999 ? '>365 días' : `${diasInvBrand.toFixed(1)} días`}
                            </td>
                            <td className="number orange">{formatNumber(brand.necesidad)}</td>
                            <td className="status-cell">
                              <div className="status-tag" style={{ color: brandStatus.color }}>
                                {brandStatus.icon}
                                <span>{brandStatus.label}</span>
                              </div>
                            </td>
                          </tr>
                          {isExpanded &&
                            brand.productos
                              .filter((p) => {
                                if (!showOnlyAlertEmergency) return true;
                                return ['Alerta', 'Emergencia'].includes(
                                  getStockStatus(p.inventario, p.necesidad).label
                                );
                              })
                              .map((prod, pIdx) => {
                                const prodStatus = getStockStatus(prod.inventario, prod.necesidad);
                                const dInv = Number(prod.dias_inventario || 0);
                                return (
                                  <tr key={`inv-prod-${bIdx}-${pIdx}`} className="child-row">
                                    <td></td>
                                    <td className="pl-30 truncate" title={prod.producto}>
                                      {prod.id_articulo} - {prod.producto}
                                    </td>
                                    <td className="number">{formatNumber(prod.cuota_forecast)}</td>
                                    <td className="number">{formatNumber(prod.real_venta)}</td>
                                    <td className="number">
                                      <div className="status-with-dot">
                                        <span>{prod.cumplimiento_pct.toFixed(0)}%</span>
                                        <div
                                          className="status-dot sm"
                                          style={{
                                            backgroundColor: getSemaforoColor(prod.cumplimiento_pct),
                                            margin: 0,
                                          }}
                                        ></div>
                                      </div>
                                    </td>
                                    <td className="number">{formatNumber(prod.inventario)}</td>
                                    <td
                                      className="number"
                                      style={{
                                        fontSize: '0.75rem',
                                        color: dInv < 15 ? '#8a0000' : '#187900',
                                      }}
                                    >
                                      {dInv >= 999 ? '>365 días' : `${dInv.toFixed(1)} días`}
                                    </td>
                                    <td className="number orange">{formatNumber(prod.necesidad)}</td>
                                    <td className="status-cell">
                                      <div className="status-tag" style={{ color: prodStatus.color }}>
                                        {prodStatus.icon}
                                        <span>{prodStatus.label}</span>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                        </React.Fragment>
                      );
                    })}

                    {/* Fila de TOTALES */}
                    {(() => {
                      const totalCumpl =
                        farmatodoInvTotals.cuota > 0
                          ? (farmatodoInvTotals.real / farmatodoInvTotals.cuota) * 100
                          : 0;
                      const totalDias =
                        farmatodoInvTotals.vd3m > 0
                          ? farmatodoInvTotals.inv / farmatodoInvTotals.vd3m
                          : 999;
                      const totalsStatus = getStockStatus(
                        farmatodoInvTotals.inv,
                        farmatodoInvTotals.nec
                      );
                      return (
                        <tr className="analysis-totals-row">
                          <td></td>
                          <td className="bold">TOTAL</td>
                          <td className="number">{formatNumber(farmatodoInvTotals.cuota)}</td>
                          <td className="number">{formatNumber(farmatodoInvTotals.real)}</td>
                          <td className="number bold">
                            <div className="status-with-dot">
                              <span>{totalCumpl.toFixed(0)}%</span>
                              <div
                                className="status-dot"
                                style={{ backgroundColor: getSemaforoColor(totalCumpl), margin: 0 }}
                              ></div>
                            </div>
                          </td>
                          <td className="number">{formatNumber(farmatodoInvTotals.inv)}</td>
                          <td
                            className="number bold"
                            style={{ color: totalDias < 15 ? '#ff4d4d' : 'inherit' }}
                          >
                            {totalDias >= 999 ? '>365 días' : `${totalDias.toFixed(1)} días`}
                          </td>
                          <td className="number orange">{formatNumber(farmatodoInvTotals.nec)}</td>
                          <td className="status-cell">
                            <div className="status-tag" style={{ color: totalsStatus.color }}>
                              {totalsStatus.icon}
                              <span>{totalsStatus.label}</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <footer className="status-footer">
          {querying
            ? 'SINCRONIZANDO...'
            : `VISTA ACTUAL: ${selectedMeses.length > 0 ? selectedMeses.join(', ') : 'TODOS'} ${
                selectedAnios.length > 0 ? selectedAnios.join(', ') : ''
              }${farmatodoInvMeta?.last_day ? ` · Último día inv: ${farmatodoInvMeta.last_day}` : ''}`}
        </footer>
      </motion.div>
    </motion.div>
  );
};

export default Farmatodo;